import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { ExperienceTemplateRenderer, ExperienceTemplateRendererContextProvider } from 'experience-template-renderer-react';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AdvertisingStore, getAdBlockerEventParams } from 'bernie-plugin-ads-loader-script';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'stores/page-layout/page-layout-store';
import { SearchEventsContextProvider } from 'src/common/stores/contexts/clickstreamEvents/search-events-context-provider';
import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';
import { View, ViewTrackerContextProvider } from 'src/common/stores/contexts/viewTracker';

import { SearchPageName } from 'constants/pages';

import { UitkSpacing } from 'uitk-react-spacing';

import { isInBrowser } from 'components/meso/utils';

import { AnnounceLoadingState } from 'components/search/announce-loading-state';
import { componentLibrary } from 'components/search/template-components';
import { MasterPage, MasterPageRoute } from 'components/layout/pages/master-page';
import { QualtricsForExperiments } from './qualtrics';
import { ShortlistProvider } from 'components/common/shortlist/shortlist-provider';
import { TemplateRenderer } from 'components/search/template-components/template-renderer';

interface SearchProps extends RouteComponentProps {
  advertising?: AdvertisingStore;
  analytics?: AnalyticsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  page?: PageStore;
  route?: MasterPageRoute;
  searchResults?: SearchResultsStore;
  uiPage?: UIPageStore;
  pageLayout?: PageLayoutStore;
}
interface SearchState {
  view: View;
}

@inject('advertising', 'analytics', 'context', 'experiment', 'multiItem', 'searchResults', 'uiPage', 'pageLayout')
@observer
class SearchComponent extends React.Component<SearchProps, SearchState> {
  constructor(props: any) {
    super(props);

    this.setView = this.setView.bind(this);

    this.state = {
      view: View.LISTINGS,
    };

    if (
      this.props.context &&
      this.props.searchResults &&
      this.props.searchResults.searchCriteria &&
      this.props.searchResults.searchCriteria.multiProp === 'true'
    ) {
      this.props.multiItem && this.props.multiItem.getOrCreateCart(this.props.context);
    }
    this.props.advertising?.includeAd();
    if (this.props.multiItem?.multiItemContext) {
      this.props.advertising?.trigger(SearchPageName[this.props.multiItem.multiItemContext.packageType]);
    } else {
      this.props.advertising?.trigger('page.Hotels.Search');
    }
  }

  get loading() {
    const { searchResults, uiPage } = this.props;

    return searchResults?.isLoading && !uiPage?.displayProgressIndicator;
  }

  componentDidMount() {
    this.trackDisambiguationSearch();
    this.trackSearchResultsError();
    this.sendAdBlockerEvent();
    // Sends performance tracking to UIS Primese
    this.props.analytics?.markPageUsable();
  }

  private analyticsTrackEvent(referrer: string, linkName: string, omnitureData: any) {
    if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
      this.props.analytics.trackEvent(referrer, linkName, omnitureData);
    }
  }

  private sendAdBlockerEvent() {
    if (isInBrowser()) {
      const { referrer, linkName } = getAdBlockerEventParams();
      this.analyticsTrackEvent(referrer, linkName, null);
    }
  }

  trackDisambiguationSearch() {
    if (this.props.multiItem && this.props.multiItem.isDisambigSearch()) {
      if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
        const events = [{ name: 'SearchResultsDisambig', error: false }];
        this.props.analytics.trackEvent(this.props.multiItem.referrer, 'package disambig search', null, events);
      }
    }
  }

  trackSearchResultsError() {
    const isEmpty = this.props.searchResults?.isEmpty;
    const isError = this.props.searchResults?.isError;

    if (isEmpty || isError) {
      this.analyticsTrackEvent('SorryMessage', 'SorryMessage', null);
    }
  }

  setView(view: View) {
    this.setState({ view });
  }

  render() {
    const { context, experiment, searchResults, multiItem, pageLayout } = this.props;
    const isMixMatchCart = multiItem && multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch';

    if (isMixMatchCart && context && multiItem && multiItem.itemCount > 0) {
      multiItem.emptyCart(context);
    }

    const controlExperience = (
      <div className="constrain-width">
        <TemplateRenderer />
      </div>
    );
    const fullPageExperience = (
      // eslint-disable-next-line camelcase
      <UitkSpacing margin={{ extra_large: { inlinestart: 'twelve', inlineend: 'twelve' } }}>
        <div>
          <TemplateRenderer />
        </div>
      </UitkSpacing>
    );

    const templateContent = experiment?.exposures.lodging_improve_page_layout?.bucket === 1 ? fullPageExperience : controlExperience;

    if (!searchResults) return;

    return (
      <ViewTrackerContextProvider viewTracker={{ view: this.state.view, setView: this.setView }}>
        <SearchEventsContextProvider searchEvents={searchResults?.trackedTraces}>
          <ShortlistProvider>
            <AnnounceLoadingState
              isLoading={this.props.searchResults?.isLoading}
              isError={this.props.searchResults?.isError}
              isEmpty={this.props.searchResults?.isEmpty}
              accessibilityLabel={this.props.searchResults?.sortAndFilter?.options?.accessibilityLabel}
            />
            <Experiment name="vrbex_split_view_50_50">
              <ExperimentControl>
                <MasterPage
                  route={this.props.route}
                  searchCriteria={searchResults?.searchCriteria}
                  pageHeader={searchResults?.resultsHeading}
                  templateContent={templateContent}
                />
              </ExperimentControl>
              <ExperimentVariant bucket={1}>
                <ExperienceTemplateRendererContextProvider library={componentLibrary}>
                  <ExperienceTemplateRenderer templateComponents={pageLayout?.getSRPTemplate('FPW_SPLIT_VIEW').components ?? []} />
                </ExperienceTemplateRendererContextProvider>
              </ExperimentVariant>
            </Experiment>
            {QualtricsForExperiments('Lodging_PWA_Qualtrics')}
            {QualtricsForExperiments('Tiered_MODs_Traveller_Experience_Hcom_Web')}
            {QualtricsForExperiments('Tiered_MOD_Traveler_Experience_BEX_Web')}
          </ShortlistProvider>
        </SearchEventsContextProvider>
      </ViewTrackerContextProvider>
    );
  }
}

const SearchComponentWithRouter = withRouter(SearchComponent);

const SearchComponentRouterWrapper = (props: SearchProps) => (
  <Experiment name="EG_Search_API_Merchandizing_flag_perf_Web">
    <ExperimentControl>
      <SearchComponentWithRouter {...props} />
    </ExperimentControl>
    <ExperimentVariant bucket={1}>
      <SearchComponent {...props} />
    </ExperimentVariant>
  </Experiment>
);

export default React.memo(SearchComponentRouterWrapper);
